import {
  ChartSpline,
  FileChartColumn,
  FlaskConical,
  ListFilter,
  MessageSquare,
  Sparkles,
  NotebookPen,
  SquarePen,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ChatEntry } from '@/contexts/ChatContext';
import type { SlotExperience } from '@/store/slotCanvasStore';
import type {
  UIBlock,
  BlockBehaviour,
  BehaviourAgentExperienceAction,
  RenderPayload,
} from '@/types/ui-blocks';

export type SlotMeta = {
  behaviourId: string;
  /** Icon to display in the navigator */
  icon: LucideIcon;
  /** Human-readable label for tooltip */
  title: string;
};

// ---------------------------------------------------------------------------
// Icon & label maps for known agent_experience types
// ---------------------------------------------------------------------------

// Keyed by SlotExperience (behaviour experiences + host-driven slot types) so
// SlotCanvasPreview can index them with any registry entry.
export const EXPERIENCE_ICON_MAP: Record<SlotExperience, LucideIcon> = {
  insight_preview: ChartSpline,
  report_preview: FileChartColumn,
  blog_refresh_queue: NotebookPen,
  content_outline_queue: NotebookPen,
  content_editor: SquarePen,
  keyword_review: ListFilter,
  experiments: FlaskConical,
  report_canvas: FileChartColumn,
  briefing: Sparkles,
};

export const EXPERIENCE_LABELS: Record<SlotExperience, string> = {
  insight_preview: 'Insights',
  report_preview: 'Report',
  blog_refresh_queue: 'Content Refresh',
  content_outline_queue: 'Content Outline',
  content_editor: 'Content Editor',
  keyword_review: 'Keyword Review',
  experiments: 'Experiment',
  report_canvas: 'Report',
  briefing: 'Briefing',
};

/** Fallback icon for chat actions */
const CHAT_ACTION_ICON: LucideIcon = MessageSquare;
/** Fallback icon for unknown / future behaviour types */
const FALLBACK_ICON: LucideIcon = Sparkles;

// ---------------------------------------------------------------------------
// Resolve icon + title for any behaviour action
// ---------------------------------------------------------------------------

function resolveSlotMeta(behaviour: BlockBehaviour): SlotMeta {
  const { action } = behaviour;

  if (action.type === 'agent_experience') {
    const agentAction = action as BehaviourAgentExperienceAction;
    return {
      behaviourId: behaviour.id,
      icon: EXPERIENCE_ICON_MAP[agentAction.experience] ?? FALLBACK_ICON,
      title:
        agentAction.data?.title ||
        EXPERIENCE_LABELS[agentAction.experience] ||
        agentAction.experience,
    };
  }

  if (action.type === 'chat') {
    // BehaviourChatAction.data only contains prompt & platform_context (no title field)
    return {
      behaviourId: behaviour.id,
      icon: CHAT_ACTION_ICON,
      title: 'Chat',
    };
  }

  // Future / unknown action types
  return {
    behaviourId: behaviour.id,
    icon: FALLBACK_ICON,
    title: 'Slot',
  };
}

// ---------------------------------------------------------------------------
// Extraction helpers
// ---------------------------------------------------------------------------

/**
 * Extract behaviour metadata from a blocks array.
 * Collects ALL auto-triggered behaviours regardless of action type.
 */
function extractFromBlocks(blocks: UIBlock[], out: SlotMeta[]) {
  for (const block of blocks) {
    const behaviours = (block as UIBlock & { behaviours?: BlockBehaviour[] })
      .behaviours;
    if (!behaviours || !Array.isArray(behaviours)) continue;

    for (const behaviour of behaviours) {
      if (behaviour.trigger !== 'auto') continue;
      out.push(resolveSlotMeta(behaviour));
    }
  }
}

/**
 * Scan messages for rendered behaviour slots and return metadata for each.
 *
 * Checks `assistant-progressive`, `assistant-structured`, and `user` messages
 * since all three can contain blocks with auto-triggered behaviours.
 */
export function extractSlotMetadata(messages: ChatEntry[]): SlotMeta[] {
  const slots: SlotMeta[] = [];

  for (const message of messages) {
    if (message.kind === 'assistant-progressive') {
      extractFromBlocks(message.blocks, slots);
    }
    if (message.kind === 'assistant-structured') {
      const payload = message.payload as RenderPayload | undefined;
      if (payload?.blocks) {
        extractFromBlocks(payload.blocks as UIBlock[], slots);
      }
    }
    if (message.kind === 'user' && message.blocks) {
      extractFromBlocks(message.blocks, slots);
    }
  }

  return slots;
}
